import { useContext, useEffect, useState } from "react"
import { ActionButton, CancelButton, Card, CommandButton, HeaderMenu, Loading,  TableSection, TabSection } from "components"
import { Acc, AddReminder, Date, Deliver, Memo, PromisedDate, Supplier, SupplierId, TaxInclusive, TradingTerm } from "../components"
import { DataJournalContextProvider } from "contexts"
import { ReminderContext } from "../contexts"
import { PurchaseOrderContext } from "./contexts"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import toast from "react-hot-toast"
import { useLocation, useParams } from "react-router-dom"
import { 
  useApi, 
  freightAlert, 
  useTaxCode,

} from "utils"
import { validation_schema, getFinalValue } from "./utils"
import { PERMISION } from "security"
import { Code, Table, AttachmentModal } from "./components"
import { JournalModals } from "../EnterBill/components"
import { useDetailBill } from "./utils/hooks/useDetailBill"
import { DetailBill, PayloadBillType } from "./types"
import { DataBillContext } from "./contexts/DetailBillContext"
import { useApprovalStore } from "./utils/store"
import { DetailBillType } from "../EnterBill/types"
import { type FormType, DetailFormType } from "./utils"
import { DataContext } from "../EnterBill/contexts"
import { tab_list } from "../EnterBill/utils"
import { Journal, Payment } from "./components/displays"

export default function EditDirectBill(): JSX.Element {
  // Hooks
  const { bill_id } = useParams()
  const { data, isLoading, refetch } = useDetailBill(bill_id)
  const { setApproval, resetApproval } = useApprovalStore()

  useEffect(() => {
    resetApproval()
    setApproval(data?.databill.approve_status || 0)
    return () => resetApproval()

  }, [data, setApproval, resetApproval])

  if (isLoading || !data) {
    return (
      <Loading
        errorText="No data available"
        loading={isLoading}
      />
    )
  }

  return (
    <DataBillContext.Provider value={{
      approve_status: data.databill.approve_status,
      approval_status: data.databill.approve_status_text,
      approved_by: data.databill.approve_by_name,
      created_by: data.databill.created_by,
      bill_status: data.databill.bill_status.toString(),
      payment_list: data.paymentbill,
      po_code: "",
      total_payment: data.databill.total_payment,
    }}>

      <FormSection
        refetch={refetch}
        transaction_date={data.databill.transaction_date}
        freight={data.databill.freight}
        trx_code={data.databill.trx_code}
        data={data}
        dataPO={null}
      />
      
    </DataBillContext.Provider>
  )
}


function FormSection(params: {
  refetch: () => void
  freight: number
  transaction_date: string
  trx_code: string
  data: PayloadBillType
  dataPO?: DetailBillType | null
}): JSX.Element {

  // Hooks
  
  const { databill, detailbill } = params.data
  const api = useApi() 
  const location = useLocation()
  const tax_code = useTaxCode()

  const mapDetailToForm = (detailbill: DetailBill[]): DetailFormType[] => {
      return detailbill.map((item) => ({
      bill_detail_id: item.bill_detail_id,
      discount: item.discount,
      mr_unit_id: String(item.unit_measure_id),
      price_bill: item.price_bill,
      product_id: String(item.product_id),
      project_id: String(item.project_id),
      quantity_bill: item.quantity_bill,
      quantity_unit: item.quantity_unit,
      tax_id: String(item.tax_id),
      total: item.total,
      vendor_description: item.vendor_description,
      description: item.vendor_description,
      }));
  };

  //Form
  const default_values : FormType = {
      bill_id: databill.bill_id,
      address: "",
      imgPath: [],
      trx_code: databill.trx_code,
      _vendor_name: databill.vendor_name ?? "",
      approval_status: databill.approve_status,
      freight: databill.freight,
      location_id: databill.location_id.toString(),
      memo: databill.memo,
      payment_term: "",
      promise_date: databill.promise_date,
      referensi: databill.referensi,
      tax_inclusive: true,
      transaction_date: databill.transaction_date,
      vendor_id: databill.vendor_id.toString(),  
      reminder: null,
      probildetail: mapDetailToForm(detailbill),
      delivery_no: databill.delivery_no,
  }

  const getSubtotalItem = (item: DetailFormType): number => {
    return item.total
  }

  const onSubmit = (value: FormType) => {
    // Vars
    const final_value = getFinalValue(value)

    return new Promise<void>((resolve) => {
      // Functions
      const submitForm = (): void => {
        toast.promise(
          api.put("/probill/edit-direct-bill", final_value),
          {
            loading: "Loading...",
            success: (res) => res.data.message,
            error: (err) => err.response.data.detail?.message ?? err.response.data.message
          }
        ).then(() => {
          methods.reset()
          params.refetch()

          // Scroll to Top
          const overflowElement = document.querySelector('#overflow-container')
          overflowElement!.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        }).catch(() => { }).finally(resolve)
      }

      if (!value.freight) {
        freightAlert("Direct Bill").then(() => {
          submitForm()
        }).catch(() => {
          return resolve()
        })
      } else {
        submitForm()
      }
    })
  }
  const methods = useForm<FormType>({
    defaultValues: default_values,
    resolver: yupResolver(validation_schema)
  })
  // console.log(methods?.control?._formValues)


  useEffect(() => {
    // Vars
    const state = location.state

    if (state) {
      methods.setValue("vendor_id", state.vendor_id)
      methods.setValue("_vendor_name", state._vendor_name)
    }

    // eslint-disable-next-line
  }, [location.state])


  //mencari total balance
  const subtotal: number = default_values.probildetail.reduce((acc: number, item: DetailFormType): number => {
    return acc + getSubtotalItem(item)
  }, 0)
  const tax: number = default_values.probildetail.reduce((acc: number, item: DetailFormType): number => {
    // Vars
      const tax_item = tax_code.data.find(i => String(i.value) === String(item.tax_id))
      return acc + (getSubtotalItem(item) * (tax_item?.rate ?? 0) / 100)
  }, 0)
  const total = subtotal + Number(databill.freight) + tax
  const totalBalance = Number(total - Number(databill.total_payment))
  //end mencari total balance

  return (
    <PurchaseOrderContext.Provider value={{ disabled: Boolean(databill.approve_status === 4) }}>
      <FormProvider {...methods}>
        <section className="container my-5 flex flex-col gap-2">
          <HeaderMenu title="DIRECT BILL" className="sticky top-[30px] z-[2]" action={databill.approve_status === 4? 'view':'edit'}>
            <Code value={databill.trx_code} />
          </HeaderMenu>

          <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3 items-end">
            <Supplier
              name="vendor_id"
              trading_term_name="payment_term"
              vendor_name="_vendor_name"
              detail="probildetail"
              disabled={Boolean(databill.approve_status === 4)}
            />

            <TradingTerm name="payment_term" />
            <TaxInclusive name="tax_inclusive" isHidden />
          </section>

          <Card>
            <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
              <section className="flex flex-col gap-3">
                <Deliver
                  disabled={Boolean(databill.approve_status === 4)}
                  deliver_name="location_id"
                  detail_name="address"
                />

                <Memo 
                  disabled={Boolean(databill.approve_status === 4)}
                  name="memo" 
                />
              </section>

              <section className="flex flex-col gap-3">
                <Date 
                  name="transaction_date" 
                  disabled={Boolean(databill.approve_status === 4)} 
                />
                <SupplierId 
                  name="referensi"
                  disabled={Boolean(databill.approve_status === 4)} 
                />
                <PromisedDate 
                  name="promise_date" 
                  disabled={Boolean(databill.approve_status === 4)} 
                />

                <ReminderContext.Provider
                  value={{
                    disabled: false,
                    transaction_from: "DIRECT_BILL",
                    name: {
                      memo: "memo",
                      promise_date: "promize_date",
                      reminder: "reminder",
                      referensi: "referensi",
                      transaction_date: "transaction_date",
                      vendor_name: "_vendor_name",
                      reminder_child: {
                        memo: "reminder.memo",
                        promise_date: "reminder.promise_date",
                        transaction_date: "reminder.transaction_date"
                      }
                    },
                    onSuccess: (value) => {
                      methods.setValue("reminder.memo", value.memo)
                      methods.setValue("reminder.promise_date", value.promise_date)
                      methods.setValue("promise_date", value.promise_date)
                      methods.setValue("reminder.transaction_date", value.transaction_date)
                    }
                  }}
                >
                  <AddReminder />
                </ReminderContext.Provider>
              </section>
            </Card.Body>
          </Card>

          <DataJournalContextProvider>
            <Table />

            <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
              <section className="flex flex-col gap-3">
               <PaymentStatus totalBalance={totalBalance}/>
              </section>

              <Acc
                name={{
                  detail: "probildetail",
                  freight: "freight"
                }}
                payment={databill.total_payment??0}
                disabled_freight={Boolean(databill.approve_status === 4)}
              />
            </section>

            {/* <ProbilContext.Provider value={{
              append: (value: DetailFormType) => {
                // Vars
                append(value)
              }
            }}> */}
              <Tab />
            {/* </ProbilContext.Provider> */}
          </DataJournalContextProvider>


          <div className="flex justify-between mt-4">
            <ActionButton>
              <section className="flex flex-wrap gap-3">
                <DataJournalContextProvider>
                  {/* <ProductContext.Provider value={{ data, isLoading }}> */}
                    <JournalModals />
                  {/* </ProductContext.Provider> */}
                </DataJournalContextProvider>

                <AttachmentModal /> 
                {/* <CommandButton actiontype="print" onClick={() => toggle()} /> */}
              </section>
            </ActionButton>

            <section className="flex flex-wrap gap-3">
                <CancelButton />

                <CommandButton
                  disabled={Boolean(databill.approve_status === 4)}
                  actiontype="save"
                  permission={PERMISION.PURCHASES.TRANSACTION.CREATE_DIRECT_BILL.CREATE}
                  loading={methods.formState.isSubmitting ? "true" : undefined}
                  onClick={methods.handleSubmit(onSubmit, (errors) => {
                    console.error("FORM VALIDATION ERROR:", errors)

                    // 1. Header error
                    if (errors.location_id?.message) {
                      toast.error(errors.location_id.message)
                      return
                    }

                    if (errors.payment_term?.message) {
                      toast.error(errors.payment_term.message)
                      return
                    }

                    // 2. Podetail error
                    if (Array.isArray(errors.probildetail) && errors.probildetail.length > 0) {
                      const rowError = errors.probildetail[0]
                      const firstFieldError:any = Object.values(rowError)[0]

                      if (firstFieldError?.message) {
                        toast.error(firstFieldError.message)
                        return
                      }
                    }

                    toast.error("Please complete required fields")
                  })}
                />
              </section>
          </div>
          
        </section>
      </FormProvider>
    </PurchaseOrderContext.Provider>
  )
}

function PaymentStatus(params:{
  totalBalance:number
}): JSX.Element {
  // Hooks
  const dataBill = useContext(DataBillContext)
  let billStatus = "Open"
  if(params.totalBalance === 0){
    billStatus = "Closed"
  }
  if(params.totalBalance < 0){
    billStatus = "Credit"
  }
  return (
    <TableSection>

      <TableSection.Row
        label="BILL STATUS"
        value={billStatus}
      />

      <TableSection.Row
        label="APPROVAL STATUS"
        value={dataBill.approval_status}
      />



      <TableSection.Row
        label="APPROVED BY"
        value={dataBill.approved_by}
      />
    </TableSection>
  )
}


function Tab(): JSX.Element {
  // Hooks
  const data = useContext(DataContext)
  const [tab, setTab] = useState<string[]>([])
  const [content, setContent] = useState<(() => JSX.Element)[]>([])
  const [currentTab, setCurrentTab] = useState<number>(0)

  // Form
  const { formState } = useFormContext<FormType>()

  useEffect(() => {
    // Vars
    const tab: string[] = [...tab_list]

    if (formState.defaultValues?.trx_code && data.approval_status !== 1) {
      tab.splice(0, 1)
      setTab(tab)
      setContent([Payment, Journal])
    }

    return () => {
      setContent([])
      setTab([])
    }

    // eslint-disable-next-line
  }, [])

  return (
    <section>
      <TabSection
        activeIndex={currentTab}
        list={tab}
        onChangeTab={setCurrentTab}
      />

      {content.map((Element, key) => {
        if (currentTab === key) {
          return <Element key={key} />
        }

        return null
      })}
    </section>
  )
}
